export type SoundKind = 'patch' | 'hit' | 'gameover' | 'start';

const MUTE_STORAGE_KEY = 'aura-portal:graph-runner:muted';

let audioContext: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const AudioContextClass =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!AudioContextClass) return null;
  if (!audioContext) {
    audioContext = new AudioContextClass();
  }
  return audioContext;
}

export function primeAudio(): void {
  const ctx = getAudioContext();
  if (ctx && ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }
}

export function isMuted(): boolean {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(MUTE_STORAGE_KEY) === 'true';
}

export function toggleMuted(): boolean {
  const next = !isMuted();
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(MUTE_STORAGE_KEY, String(next));
  }
  return next;
}

function playNote(
  ctx: AudioContext,
  frequency: number,
  startTime: number,
  duration: number,
  type: OscillatorType,
  gainPeak: number,
) {
  const oscillator = ctx.createOscillator();
  const gain = ctx.createGain();
  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, startTime);
  gain.gain.setValueAtTime(0, startTime);
  gain.gain.linearRampToValueAtTime(gainPeak, startTime + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);
  oscillator.connect(gain);
  gain.connect(ctx.destination);
  oscillator.start(startTime);
  oscillator.stop(startTime + duration + 0.02);
}

export function playTone(kind: SoundKind): void {
  if (isMuted()) return;
  const ctx = getAudioContext();
  if (!ctx || ctx.state === 'suspended') return;
  const now = ctx.currentTime;

  switch (kind) {
    case 'patch':
      playNote(ctx, 880, now, 0.09, 'triangle', 0.06);
      playNote(ctx, 1320, now + 0.05, 0.1, 'triangle', 0.04);
      break;
    case 'hit':
      playNote(ctx, 220, now, 0.16, 'sawtooth', 0.08);
      playNote(ctx, 110, now + 0.06, 0.2, 'sawtooth', 0.06);
      break;
    case 'gameover':
      playNote(ctx, 392, now, 0.18, 'sine', 0.07);
      playNote(ctx, 329, now + 0.16, 0.18, 'sine', 0.07);
      playNote(ctx, 261, now + 0.32, 0.3, 'sine', 0.07);
      break;
    case 'start':
      playNote(ctx, 440, now, 0.08, 'triangle', 0.05);
      playNote(ctx, 660, now + 0.07, 0.12, 'triangle', 0.05);
      break;
    default:
      break;
  }
}
